import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { LogOut, ChevronLeft, Trash2, Copy, Download, Loader2 } from "lucide-react";
import jsPDF from "jspdf";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_NAMES = {
  clarity: "Clarity Room",
  asset_builder: "Asset Builder",
  authority: "Authority Assets",
  launch: "Calm Launch"
};

const GENERATOR_NAMES = {
  vision_statement: "Vision Statement",
  ideal_client: "Ideal Client Profile",
  core_offer: "Core Offer Definition",
  revenue_mapping: "Revenue Goal Mapping",
  lead_magnet: "Lead Magnet Blueprint",
  content_pillars: "Content Pillar Structure",
  email_sequence: "Email Sequence Planner",
  product_outline: "Product Outline",
  bio_generator: "Bio Generator",
  testimonial_framework: "Testimonial Framework",
  case_study: "Case Study Template",
  media_kit: "Media Kit Creator",
  launch_timeline: "Launch Timeline",
  pricing_strategy: "Pricing Strategy",
  objection_handler: "Objection Handler",
  launch_copy: "Launch Copy Generator"
};

export default function SavedOutputs() {
  const { user, logout } = useAuth();
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);

  const getInitials = (name) => {
    if (!name) return "CS";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  useEffect(() => {
    fetchOutputs();
  }, [filter]);

  const fetchOutputs = async () => {
    try {
      const url = filter === "all" 
        ? `${API_URL}/api/outputs`
        : `${API_URL}/api/outputs?room=${filter}`;
      
      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setOutputs(data);
      }
    } catch (error) {
      console.error('Failed to fetch outputs:', error);
      toast.error("Failed to load saved outputs.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (outputId) => {
    setDeletingId(outputId);
    try {
      const response = await fetch(`${API_URL}/api/outputs/${outputId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setOutputs(prev => prev.filter(o => o.output_id !== outputId));
        toast.success("Output deleted.");
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      toast.error("Failed to delete.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopy = async (output) => {
    try {
      await navigator.clipboard.writeText(output.output);
      toast.success("Copied to clipboard.");
    } catch (error) {
      toast.error("Failed to copy.");
    }
  };

  const handleDownloadPDF = (output) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    
    doc.setFont("times", "bold");
    doc.setFontSize(24);
    doc.setTextColor(44, 44, 44);
    doc.text(output.title, margin, 30);
    
    doc.setFont("times", "italic");
    doc.setFontSize(10);
    doc.setTextColor(107, 142, 124);
    doc.text(`${ROOM_NAMES[output.room]} • ${GENERATOR_NAMES[output.generator]}`, margin, 40);
    
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.setTextColor(44, 44, 44);
    
    const lines = doc.splitTextToSize(output.output, maxWidth);
    let y = 55;
    
    lines.forEach(line => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(line, margin, y);
      y += 6;
    });
    
    doc.setFont("times", "italic");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text("Generated by Calm Scale™", margin, 285);
    
    doc.save(`${output.title.replace(/\s+/g, '_')}.pdf`);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-warm border-b border-[#E5E0D6]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/journey" 
              className="flex items-center gap-2 text-[#2C2C2C]/60 hover:text-[#6B8E7C] transition-colors"
              data-testid="back-to-dashboard"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm hidden sm:inline">Blueprint</span>
            </Link>
            <span className="text-[#E5E0D6]">|</span>
            <span className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Saved Outputs
            </span>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="saved-user-menu">
                <Avatar className="h-10 w-10 border-2 border-[#E5E0D6]">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#6B8E7C] text-white text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-white border-[#E5E0D6]" align="end">
              <div className="px-3 py-2 border-b border-[#E5E0D6]">
                <p className="text-sm font-medium text-[#2C2C2C]">{user?.name}</p>
                <p className="text-xs text-[#2C2C2C]/50">{user?.email}</p>
              </div>
              <DropdownMenuItem 
                onClick={logout}
                className="cursor-pointer text-[#2C2C2C]/70 hover:text-[#2C2C2C] focus:bg-[#F2F0EB]"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-up opacity-0" style={{ animationFillMode: 'forwards' }}>
            <div>
              <h1 className="text-3xl md:text-4xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your Progress
              </h1>
              <p className="text-[#2C2C2C]/60 font-light">
                All your generated content, saved in one place.
              </p>
            </div>
            
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px] bg-white border-[#E5E0D6]" data-testid="room-filter">
                <SelectValue placeholder="Filter by room" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E5E0D6]">
                <SelectItem value="all">All Rooms</SelectItem>
                <SelectItem value="clarity">Clarity Room</SelectItem>
                <SelectItem value="asset_builder">Asset Builder</SelectItem>
                <SelectItem value="authority">Authority Assets</SelectItem>
                <SelectItem value="launch">Calm Launch</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Outputs List */}
          {loading ? (
            <div className="text-center py-20">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-[#2C2C2C]/60 font-light">Loading your progress...</p>
            </div>
          ) : outputs.length === 0 ? (
            <div className="text-center py-20 card-calm animate-fade-up opacity-0" style={{ animationFillMode: 'forwards' }}>
              <p className="text-[#2C2C2C]/60 text-lg mb-4">No saved outputs yet.</p>
              <p className="text-[#2C2C2C]/40 text-sm mb-6">
                Generate content in any room and save it to see it here.
              </p>
              <Link to="/journey">
                <Button className="btn-primary">
                  Explore Phases
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {outputs.map((output, index) => (
                <div 
                  key={output.output_id}
                  className="card-calm animate-fade-up opacity-0"
                  style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
                  data-testid={`saved-output-${output.output_id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {output.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-1 rounded-full bg-[#6B8E7C]/10 text-[#6B8E7C]">
                          {ROOM_NAMES[output.room]}
                        </span>
                        <span className="text-xs text-[#2C2C2C]/40">
                          {GENERATOR_NAMES[output.generator]}
                        </span>
                        <span className="text-xs text-[#2C2C2C]/30">•</span>
                        <span className="text-xs text-[#2C2C2C]/40">
                          {formatDate(output.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopy(output)}
                        className="btn-ghost h-8 w-8 p-0"
                        data-testid={`copy-saved-${output.output_id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadPDF(output)}
                        className="btn-ghost h-8 w-8 p-0"
                        data-testid={`download-saved-${output.output_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(output.output_id)}
                        disabled={deletingId === output.output_id}
                        className="btn-ghost h-8 w-8 p-0 text-red-400 hover:text-red-500 hover:bg-red-50"
                        data-testid={`delete-saved-${output.output_id}`}
                      >
                        {deletingId === output.output_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="text-sm text-[#2C2C2C]/70 line-clamp-3 output-document">
                    {output.output}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
